import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ajustarTextos,
  calcularSnap,
  handlesDe,
  LIENZOS,
  moverRect,
  type Restricciones,
  Poster,
  redimensionarRect,
  type Guia,
  type Handle,
  type PosterElement,
  type PosterResuelto,
  type Rect,
} from '@postmaker/poster';

/**
 * Superficie de edición compartida (empresa y cliente). Pointer Events (regla 10).
 * v2: el gesto vive en listeners de WINDOW con estado en refs — el arrastre no se
 * corta aunque React re-renderice o el puntero salga del lienzo. Multi-selección con
 * shift+click y arrastre en grupo.
 */
export interface PropsLienzoEditor {
  resuelto: PosterResuelto;
  /** Transformación SOLO para pintar (p. ej. tokens de color de marca); nunca se commitea. */
  pintarElementos?: (els: PosterElement[]) => PosterElement[];
  elementos: PosterElement[];
  onCambiar: (elementos: PosterElement[]) => void;
  onCommit: (previo: PosterElement[]) => void;
  seleccion: string[];
  onSeleccion: (ids: string[]) => void;
  escala: number;
  modo: 'empresa' | 'cliente';
  onEditarTexto?: (id: string) => void;
}

interface Gesto {
  tipo: 'mover' | 'redimensionar' | 'rotar';
  handle?: Handle;
  origenX: number;
  origenY: number;
  /** rects iniciales de TODOS los seleccionados (arrastre en grupo). */
  rectsIniciales: Map<string, Rect>;
  idPrimario: string;
  rotacionInicial: number;
  centroX: number;
  centroY: number;
  snapshot: PosterElement[];
  movio: boolean;
}

export function LienzoEditor({
  resuelto,
  pintarElementos,
  elementos,
  onCambiar,
  onCommit,
  seleccion,
  onSeleccion,
  escala,
  modo,
  onEditarTexto,
}: PropsLienzoEditor) {
  const lienzo = LIENZOS[resuelto.formato];
  // La empresa diseña libre: las figuras pueden salir del lienzo (lo que sobresale
  // se recorta al pintar). Al cliente lo siguen protegiendo sus restricciones (D22).
  const liberar = (r: Restricciones): Restricciones =>
    modo === 'empresa' ? { ...r, dentroDelLienzo: false } : r;
  const contenedorRef = useRef<HTMLDivElement>(null);
  const gestoRef = useRef<Gesto | null>(null);
  const [guias, setGuias] = useState<Guia[]>([]);

  // Refs espejo: los listeners de window leen SIEMPRE el estado vigente.
  const elementosRef = useRef(elementos);
  elementosRef.current = elementos;
  const seleccionRef = useRef(seleccion);
  seleccionRef.current = seleccion;
  const propsRef = useRef({ onCambiar, onCommit, escala, lienzo, modo });
  propsRef.current = { onCambiar, onCommit, escala, lienzo, modo };

  // Con el dedo no hay precisión de píxel: imán más fuerte (§6.4).
  const umbralSnap = modo === 'cliente' ? 1.6 : 0.9;

  // Auto-ajuste de texto en vivo: la misma función que corre en Chromium.
  useEffect(() => {
    const nodo = contenedorRef.current;
    if (!nodo) return;
    const id = requestAnimationFrame(() => {
      // ajustarTextos es idempotente: parte del data-tamano de cada texto.
      ajustarTextos(nodo);
    });
    return () => cancelAnimationFrame(id);
  }, [elementos, resuelto]);

  const esEditable = useCallback(
    (el: PosterElement | undefined) => Boolean(el && !(el.bloqueado && propsRef.current.modo === 'cliente')),
    [],
  );

  // ── Motor del gesto: window listeners, estado en refs ─────────────────────
  const alMoverGlobal = useCallback((ev: PointerEvent) => {
    const gesto = gestoRef.current;
    if (!gesto) return;
    const { escala: esc, lienzo: lz, onCambiar: cambiar } = propsRef.current;
    const actuales = elementosRef.current;
    const primario = actuales.find((e) => e.id === gesto.idPrimario);
    if (!primario) return;
    gesto.movio = true;

    if (gesto.tipo === 'rotar') {
      const angulo =
        (Math.atan2(ev.clientY - gesto.centroY, ev.clientX - gesto.centroX) * 180) / Math.PI + 90;
      const ajustado = ev.shiftKey ? Math.round(angulo / 15) * 15 : Math.round(angulo);
      cambiar(actuales.map((e) => (e.id === primario.id ? { ...e, rotacion: ajustado } : e)));
      return;
    }

    const dx = ((ev.clientX - gesto.origenX) / (lz.w * esc)) * 100;
    const dy = ((ev.clientY - gesto.origenY) / (lz.h * esc)) * 100;

    if (gesto.tipo === 'mover') {
      const rectInicialPrimario = gesto.rectsIniciales.get(primario.id)!;
      const movidoPrimario = moverRect(rectInicialPrimario, dx, dy, liberar(primario.restricciones));
      const otros = actuales
        .filter((e) => !gesto.rectsIniciales.has(e.id) && e.visible)
        .map((e) => e.rect);
      const snap = calcularSnap(movidoPrimario, otros, gesto.rectsIniciales.size > 1 ? 0 : umbralSnapRef.current);
      const dxFinal = dx + snap.dx;
      const dyFinal = dy + snap.dy;
      setGuiasRef.current(snap.guias);

      cambiar(
        actuales.map((e) => {
          const inicial = gesto.rectsIniciales.get(e.id);
          if (!inicial) return e;
          return { ...e, rect: moverRect(inicial, dxFinal, dyFinal, liberar(e.restricciones)) };
        }),
      );
      return;
    }

    if (gesto.tipo === 'redimensionar' && gesto.handle) {
      const restricciones = liberar(
        ev.shiftKey && !primario.restricciones.proporcionFija
          ? { ...primario.restricciones, proporcionFija: true }
          : primario.restricciones,
      );
      const rectInicial = gesto.rectsIniciales.get(primario.id)!;
      const nuevo = redimensionarRect(rectInicial, gesto.handle, dx, dy, restricciones, lz);
      cambiar(actuales.map((e) => (e.id === primario.id ? { ...e, rect: nuevo } : e)));
    }
  }, []);

  const alSoltarGlobal = useCallback(() => {
    const gesto = gestoRef.current;
    gestoRef.current = null;
    window.removeEventListener('pointermove', alMoverGlobal);
    window.removeEventListener('pointerup', alSoltarGlobal);
    window.removeEventListener('pointercancel', alSoltarGlobal);
    setGuiasRef.current([]);
    // El commit entrega el estado PREVIO al gesto para el historial.
    if (gesto?.movio) propsRef.current.onCommit(gesto.snapshot);
  }, [alMoverGlobal]);

  // refs auxiliares estables para los callbacks de window
  const setGuiasRef = useRef(setGuias);
  setGuiasRef.current = setGuias;
  const umbralSnapRef = useRef(umbralSnap);
  umbralSnapRef.current = umbralSnap;

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', alMoverGlobal);
      window.removeEventListener('pointerup', alSoltarGlobal);
      window.removeEventListener('pointercancel', alSoltarGlobal);
    },
    [alMoverGlobal, alSoltarGlobal],
  );

  function iniciarGesto(gesto: Omit<Gesto, 'snapshot' | 'movio'>) {
    gestoRef.current = { ...gesto, snapshot: elementosRef.current, movio: false };
    window.addEventListener('pointermove', alMoverGlobal);
    window.addEventListener('pointerup', alSoltarGlobal);
    window.addEventListener('pointercancel', alSoltarGlobal);
  }

  function alBajarSobreElemento(ev: React.PointerEvent, id: string) {
    const el = elementosRef.current.find((e) => e.id === id);
    if (!el || !esEditable(el)) return; // bloqueado para el cliente: ni se selecciona
    ev.stopPropagation();
    ev.preventDefault();

    // shift+click: agrega/quita de la selección (agrupación ad hoc).
    let ids: string[];
    const actual = seleccionRef.current;
    if (ev.shiftKey) {
      ids = actual.includes(id) ? actual.filter((x) => x !== id) : [...actual, id];
      onSeleccion(ids);
      if (!ids.includes(id)) return; // se quitó: no arranca gesto
    } else {
      ids = actual.includes(id) ? actual : [id];
      onSeleccion(ids);
    }

    const rects = new Map<string, Rect>();
    for (const sid of ids) {
      const sel = elementosRef.current.find((e) => e.id === sid);
      if (sel && esEditable(sel)) rects.set(sid, { ...sel.rect });
    }

    iniciarGesto({
      tipo: 'mover',
      origenX: ev.clientX,
      origenY: ev.clientY,
      rectsIniciales: rects,
      idPrimario: id,
      rotacionInicial: el.rotacion,
      centroX: 0,
      centroY: 0,
    });
  }

  function alBajarHandle(ev: React.PointerEvent, handle: Handle) {
    const id = seleccionRef.current[0];
    const el = id ? elementosRef.current.find((e) => e.id === id) : undefined;
    if (!el || !esEditable(el)) return;
    ev.stopPropagation();
    ev.preventDefault();
    iniciarGesto({
      tipo: 'redimensionar',
      handle,
      origenX: ev.clientX,
      origenY: ev.clientY,
      rectsIniciales: new Map([[el.id, { ...el.rect }]]),
      idPrimario: el.id,
      rotacionInicial: el.rotacion,
      centroX: 0,
      centroY: 0,
    });
  }

  function alBajarRotador(ev: React.PointerEvent) {
    const id = seleccionRef.current[0];
    const el = id ? elementosRef.current.find((e) => e.id === id) : undefined;
    if (!el || !esEditable(el)) return;
    if (modo === 'cliente' && !el.restricciones.rotable) return; // §5.5
    ev.stopPropagation();
    ev.preventDefault();
    const caja = contenedorRef.current!.getBoundingClientRect();
    iniciarGesto({
      tipo: 'rotar',
      origenX: ev.clientX,
      origenY: ev.clientY,
      rectsIniciales: new Map([[el.id, { ...el.rect }]]),
      idPrimario: el.id,
      rotacionInicial: el.rotacion,
      centroX: caja.left + ((el.rect.x + el.rect.w / 2) / 100) * lienzo.w * escala,
      centroY: caja.top + ((el.rect.y + el.rect.h / 2) / 100) * lienzo.h * escala,
    });
  }

  const primario = elementos.find((e) => e.id === seleccion[0]) ?? null;

  return (
    <div
      className="lienzo-tactil relative"
      style={{ width: lienzo.w * escala, height: lienzo.h * escala }}
      onPointerDown={() => onSeleccion([])}
    >
      <div ref={contenedorRef} className="absolute inset-0 overflow-hidden shadow-lg">
        <Poster
          resuelto={{ ...resuelto, elementos: pintarElementos ? pintarElementos(elementos) : elementos }}
          escala={escala}
          atributosElemento={(el) => ({
            onPointerDown: (ev: React.PointerEvent) => alBajarSobreElemento(ev, el.id),
            onDoubleClick: () => el.tipo === 'texto' && onEditarTexto?.(el.id),
            style: {
              cursor: modo === 'cliente' && el.bloqueado ? 'default' : 'move',
            },
          })}
        />
      </div>

      {/* Guías de snapping */}
      {guias.map((g, i) =>
        g.eje === 'x' ? (
          <div key={i} className="pointer-events-none absolute top-0 h-full w-px bg-pink-500" style={{ left: `${g.pos}%` }} />
        ) : (
          <div key={i} className="pointer-events-none absolute left-0 w-full border-t border-pink-500" style={{ top: `${g.pos}%` }} />
        ),
      )}

      {/* Contornos de la selección secundaria */}
      {elementos
        .filter((e) => seleccion.includes(e.id) && e.id !== primario?.id)
        .map((e) => (
          <div
            key={e.id}
            className="pointer-events-none absolute border-2 border-sky-400/70"
            style={{
              left: `${e.rect.x}%`,
              top: `${e.rect.y}%`,
              width: `${e.rect.w}%`,
              height: `${e.rect.h}%`,
              transform: e.rotacion ? `rotate(${e.rotacion}deg)` : undefined,
            }}
          />
        ))}

      {/* Selección primaria + handles (sólo con selección simple) */}
      {primario && esEditable(primario) ? (
        <div
          className="pointer-events-none absolute border-2 border-sky-500"
          style={{
            left: `${primario.rect.x}%`,
            top: `${primario.rect.y}%`,
            width: `${primario.rect.w}%`,
            height: `${primario.rect.h}%`,
            transform: primario.rotacion ? `rotate(${primario.rotacion}deg)` : undefined,
          }}
        >
          {seleccion.length === 1
            ? handlesDe(primario.restricciones).map((h) => (
                <span
                  key={h}
                  onPointerDown={(ev) => alBajarHandle(ev, h)}
                  // handle de 12px con área táctil de 44px (§6.4)
                  className="pointer-events-auto absolute flex h-11 w-11 items-center justify-center"
                  style={posicionHandle(h)}
                >
                  <span className="block h-3 w-3 rounded-full border-2 border-sky-500 bg-superficie" />
                </span>
              ))
            : null}
          {seleccion.length === 1 &&
          (modo === 'empresa' || primario.restricciones.rotable) &&
          primario.tipo !== 'contactos' ? (
            <span
              onPointerDown={alBajarRotador}
              className="pointer-events-auto absolute left-1/2 top-0 flex h-11 w-11 -translate-x-1/2 -translate-y-full items-center justify-center"
            >
              <span className="block h-3 w-3 rounded-full border-2 border-emerald-500 bg-superficie" />
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function posicionHandle(h: Handle): React.CSSProperties {
  const mitad = '-1.375rem';
  const estilo: React.CSSProperties = {};
  if (h.includes('n')) estilo.top = mitad;
  if (h.includes('s')) estilo.bottom = mitad;
  if (h.includes('w')) estilo.left = mitad;
  if (h.includes('e')) estilo.right = mitad;
  if (h === 'n' || h === 's') {
    estilo.left = '50%';
    estilo.transform = 'translateX(-50%)';
  }
  if (h === 'e' || h === 'w') {
    estilo.top = '50%';
    estilo.transform = 'translateY(-50%)';
  }
  return estilo;
}
